import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetDraftPreviewTokenOptions,
  GetDraftPreviewTokenResponse,
  GetRouterPagesCachedOptions,
  GetRouterPagesOptions,
  GetRouterPagesResponse,
  GetRouterSitemapCountOptions,
  GetRouterSitemapCountResponse,
  GetRouterSitemapOptions,
  GetRouterSitemapResponse,
  getDraftPreviewToken as universalGetDraftPreviewToken,
  getRouterPages as universalGetRouterPages,
  getRouterPagesCached as universalGetRouterPagesCached,
  getRouterSitemap as universalGetRouterSitemap,
  getRouterSitemapCount as universalGetRouterSitemapCount,
} from './dynamic-page-router-v1-page-pages.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/dynamic-page-router' };

export function getRouterSitemapCount(
  httpClient: HttpClient
): GetRouterSitemapCountSignature {
  return (options?: GetRouterSitemapCountOptions) =>
    universalGetRouterSitemapCount(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetRouterSitemapCountSignature {
  /**
   * Returns the count of sitemap entries grouped by page name.
   *
   * Use this endpoint for analytics dashboards, capacity planning, and understanding
   * the scale of dynamic content without retrieving actual URLs. Results are capped
   * at 1,000 items per pattern for performance.
   */
  (options?: GetRouterSitemapCountOptions): Promise<
    NonNullablePaths<
      GetRouterSitemapCountResponse,
      `result` | `result.${number}.pageName` | `result.${number}.count`,
      4
    >
  >;
}

export function getRouterSitemap(
  httpClient: HttpClient
): GetRouterSitemapSignature {
  return (options?: GetRouterSitemapOptions) =>
    universalGetRouterSitemap(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetRouterSitemapSignature {
  /**
   * Returns sitemap entries containing URLs, page names, and page titles.
   *
   * Use this endpoint to generate XML sitemaps for search engines, build navigation
   * menus, or provide URL listings to external systems. The `fullUrl` parameter
   * provides context for scoping sitemap generation to specific domains or URL hierarchies.
   */
  (options?: GetRouterSitemapOptions): Promise<
    NonNullablePaths<
      GetRouterSitemapResponse,
      | `result`
      | `result.${number}.url`
      | `result.${number}.pageName`
      | `result.${number}.title`
      | `result.${number}.changeFrequency`
      | `result.${number}.priority`,
      4
    >
  >;
}

export function getRouterPages(
  httpClient: HttpClient
): GetRouterPagesSignature {
  return (options?: GetRouterPagesOptions) =>
    universalGetRouterPages(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetRouterPagesSignature {
  /**
   * Returns comprehensive page information for a specific route.
   * Called by the editor via POST with flat body + query params.
   */
  (options?: GetRouterPagesOptions): Promise<
    NonNullablePaths<
      GetRouterPagesResponse,
      | `result.status`
      | `result.page`
      | `result.head.title`
      | `result.head.description`
      | `result.head.keywords`
      | `result.head.noIndex`
      | `result.message`
      | `result.redirectUrl`,
      4
    >
  >;
}

export function getRouterPagesCached(
  httpClient: HttpClient
): GetRouterPagesCachedSignature {
  return (options?: GetRouterPagesCachedOptions) =>
    universalGetRouterPagesCached(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetRouterPagesCachedSignature {
  /**
   * Returns comprehensive page information for a specific route (cacheable).
   * Called by the viewer via GET with .r parameter containing {urlParams, body} envelope.
   */
  (options?: GetRouterPagesCachedOptions): Promise<
    NonNullablePaths<
      GetRouterPagesResponse,
      | `result.status`
      | `result.page`
      | `result.head.title`
      | `result.head.description`
      | `result.head.keywords`
      | `result.head.noIndex`
      | `result.message`
      | `result.redirectUrl`,
      4
    >
  >;
}

export function getDraftPreviewToken(
  httpClient: HttpClient
): GetDraftPreviewTokenSignature {
  return (options?: GetDraftPreviewTokenOptions) =>
    universalGetDraftPreviewToken(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDraftPreviewTokenSignature {
  /**
   * Returns the preview token used for draft collection previews.
   */
  (options?: GetDraftPreviewTokenOptions): Promise<
    NonNullablePaths<GetDraftPreviewTokenResponse, `token`, 2>
  >;
}

export {
  Config,
  GetDraftPreviewTokenOptions,
  GetDraftPreviewTokenRequest,
  GetDraftPreviewTokenResponse,
  GetRouterPagesCachedOptions,
  GetRouterPagesCachedRequest,
  GetRouterPagesCachedUrlParams,
  GetRouterPagesOptions,
  GetRouterPagesRequest,
  GetRouterPagesResponse,
  GetRouterSitemapCountOptions,
  GetRouterSitemapCountRequest,
  GetRouterSitemapCountResponse,
  GetRouterSitemapOptions,
  GetRouterSitemapRequest,
  GetRouterSitemapResponse,
  Page,
  PageRole,
  PageRule,
  Pattern,
  PatternConfig,
  RequestInfo,
  RouterCountItem,
  RouterPageHead,
  RouterPagesResult,
  RouterRouteItem,
} from './dynamic-page-router-v1-page-pages.universal.js';
